// NestedCell — generic renderer for one payload value in a table cell.
//
// Operator goal (per "recordi + dubine" request 2026-05-12): instead of
// asking operators to flatten nested webhook payloads per source, show
// the nested shape inline with collapsible blocks. Works for Stripe,
// Pipedrive, Mailchimp, Salesforce, any nested-JSON source — zero
// per-source code.
//
// Behaviour by JS runtime type:
//   - null / undefined   → "—" (dim placeholder)
//   - scalar             → bare value, truncated past 80 chars
//   - array              → "[N items]" type-chip, click → drawer/modal
//   - object             → collapsible chip with:
//                            - up to 2 most-occurring scalar children
//                              summarised as "k=v · k=v"
//                            - "+M" overflow for the rest
//                            - "▾" affordance; click → row inline-
//                              expands a key=value chip grid
//   - oversized objects  → "{N keys}" placeholder when cardinality
//                          exceeded SCHEMA_CARDINALITY_COLLAPSE on the
//                          backend (collapsed_cardinality hint from
//                          events:schema). Same expand affordance.
//
// Type-color comes from a type→hue mapping, NOT from the value itself
// — operator can't tell "deal" from "person" by looking at a cell, only
// by reading content. Visual cue is "this is an object", "this is an
// array". Pair with TagChip in the input-tags column (per-(k,v) hue)
// for the source-of-data colour signal.

import * as React from 'react';
import { TagChip, colorForTag } from './tag-colors';

type Kind = 'null' | 'scalar' | 'array' | 'object';

function runtimeKind(v: unknown): Kind {
  if (v === null || v === undefined) return 'null';
  if (Array.isArray(v)) return 'array';
  if (typeof v === 'object') return 'object';
  return 'scalar';
}

const TYPE_COLOUR: Record<Exclude<Kind, 'null' | 'scalar'>, { bg: string; fg: string; border: string }> = {
  object: { bg: 'oklch(0.95 0.04 235 / 0.6)', fg: 'oklch(0.42 0.16 235)', border: 'oklch(0.85 0.10 235)' },
  array:  { bg: 'oklch(0.95 0.05 65 / 0.6)',  fg: 'oklch(0.45 0.18 65)',  border: 'oklch(0.85 0.10 65)' },
};

/** Shorten a scalar for display. Strings >80 chars truncate; everything
 *  else is .toString(). */
function summariseScalar(v: unknown): string {
  if (typeof v === 'string') return v.length > 80 ? v.slice(0, 77) + '…' : v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return JSON.stringify(v);
}

/** Pick up to 2 scalar entries from an object to show in the collapsed
 *  chip preview. Returns most-occurring keys first by string-sort
 *  (stable across renders), filtering out children that are themselves
 *  objects/arrays. Pure structural — no per-source heuristic. */
function pickScalarPreview(obj: Record<string, unknown>, limit = 2): Array<[string, unknown]> {
  const entries = Object.entries(obj)
    .filter(([, v]) => runtimeKind(v) === 'scalar')
    .sort(([a], [b]) => a.localeCompare(b));
  return entries.slice(0, limit);
}

/** Count distinct keys in an object — used for the "+N" overflow and
 *  for the {N keys} placeholder on cardinality-collapsed paths. */
function keyCount(v: unknown): number {
  if (v === null || v === undefined) return 0;
  if (Array.isArray(v)) return v.length;
  if (typeof v === 'object') return Object.keys(v as object).length;
  return 0;
}

/** Read a dot-path from a value. Tolerant: missing intermediate keys
 *  → undefined; arrays not indexed. */
export function readPath(v: unknown, path: string): unknown {
  if (!path) return v;
  let cur: unknown = v;
  for (const part of path.split('.')) {
    if (cur === null || cur === undefined || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[part];
  }
  return cur;
}

// ── Component ──────────────────────────────────────────────────────

export interface NestedCellProps {
  value: unknown;
  /** Hint from events:schema response. When the backend reported the
   *  parent's cardinality exceeded the collapse threshold, pass the
   *  count so we render "{N keys}" placeholder instead of attempting
   *  preview. */
  collapsedCardinality?: number;
  /** Optional cap on expand depth from here. Useful when a row-level
   *  expand is inline; pass 1 to allow only one level of click-through. */
  maxExpandDepth?: number;
  /** Compact rendering for dense tables. */
  compact?: boolean;
  /** Path prefix carried through recursion so the per-key "pin as
   *  column" button can report a full dotted path (e.g.
   *  "current.title"). Internal — V4 EventsTable passes the top-level
   *  key like "current" or "meta"; recursion extends it. */
  pathPrefix?: string;
  /** When set, NestedCell renders a tiny "+col" button next to each
   *  key in the expand panel. Click calls back with the full dotted
   *  path so the caller can pin it as a top-level column in their
   *  table. Operator-driven column promotion — B3 drill-in. */
  onPinPath?: (path: string) => void;
  /** Paths the caller already considers pinned. Their "+col" buttons
   *  render as "✓" (still clickable to unpin via the caller). */
  pinnedPaths?: Set<string>;
}

export function NestedCell({
  value, collapsedCardinality, maxExpandDepth = 2, compact,
  pathPrefix = '', onPinPath, pinnedPaths,
}: NestedCellProps): React.ReactElement {
  const [expanded, setExpanded] = React.useState(false);
  const kind = runtimeKind(value);

  // null / undefined — placeholder
  if (kind === 'null') {
    return <span style={{ color: 'var(--ink-3)' }}>—</span>;
  }

  // scalar — bare
  if (kind === 'scalar') {
    return <span style={{ fontFamily: 'var(--mono)', fontSize: compact ? 11.5 : 12, color: 'var(--ink-2)' }}>{summariseScalar(value)}</span>;
  }

  // array — type chip + drawer affordance (drawer expansion is up to caller)
  if (kind === 'array') {
    const n = (value as unknown[]).length;
    const tone = TYPE_COLOUR.array;
    return (
      <button
        type="button"
        onClick={(ev) => { ev.stopPropagation(); setExpanded(e => !e); }}
        title={`Array of ${n} item${n === 1 ? '' : 's'}`}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: compact ? '1px 6px' : '2px 8px',
          fontFamily: 'var(--mono)', fontSize: compact ? 10.5 : 11,
          background: tone.bg, border: `1px solid ${tone.border}`, color: tone.fg,
          borderRadius: 3, cursor: 'pointer',
        }}
      >
        [{n} {n === 1 ? 'item' : 'items'}]
        <span aria-hidden style={{ opacity: 0.6 }}>{expanded ? '▴' : '▾'}</span>
        {expanded && <span style={{ marginLeft: 6, color: 'var(--ink-3)' }}>(array expansion: use array_expand trigger)</span>}
      </button>
    );
  }

  // object — chip with preview + expand
  const obj = value as Record<string, unknown>;
  const totalKeys = collapsedCardinality ?? keyCount(value);
  const isCollapsedByBackend = collapsedCardinality !== undefined;
  const preview = isCollapsedByBackend ? [] : pickScalarPreview(obj, 2);
  const overflow = totalKeys - preview.length;
  const tone = TYPE_COLOUR.object;

  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start', maxWidth: '100%' }}>
      <button
        type="button"
        onClick={(ev) => { ev.stopPropagation(); setExpanded(e => !e); }}
        title={isCollapsedByBackend
          ? `${totalKeys} keys — cardinality above threshold, drill in to promote columns`
          : `${totalKeys} key${totalKeys === 1 ? '' : 's'} — click to expand`}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: compact ? '1px 6px' : '2px 8px',
          fontFamily: 'var(--mono)', fontSize: compact ? 10.5 : 11,
          background: tone.bg, border: `1px solid ${tone.border}`, color: tone.fg,
          borderRadius: 3, cursor: 'pointer', maxWidth: '100%',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}
      >
        {isCollapsedByBackend ? (
          <span>{`{${totalKeys} keys}`}</span>
        ) : preview.length === 0 ? (
          <span>{`{${totalKeys} key${totalKeys === 1 ? '' : 's'}}`}</span>
        ) : (
          <>
            {preview.map(([k, v], i) => (
              <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                {i > 0 && <span style={{ opacity: 0.4 }}>·</span>}
                <span style={{ opacity: 0.6 }}>{k}=</span>
                <span>{summariseScalar(v).slice(0, 24)}</span>
              </span>
            ))}
            {overflow > 0 && <span style={{ opacity: 0.55 }}>+{overflow}</span>}
          </>
        )}
        <span aria-hidden style={{ opacity: 0.6, marginLeft: 2 }}>{expanded ? '▴' : '▾'}</span>
      </button>
      {expanded && maxExpandDepth > 0 && (
        <ObjectExpandPanel
          value={obj}
          maxExpandDepth={maxExpandDepth}
          compact={compact}
          pathPrefix={pathPrefix}
          onPinPath={onPinPath}
          pinnedPaths={pinnedPaths}
        />
      )}
    </span>
  );
}

/** Inline panel showing every key=value pair under an expanded object.
 *  Recursion: each value renders through NestedCell, so a depth-2
 *  nested object becomes another collapsible chip. Stops at
 *  maxExpandDepth-1 to avoid runaway expand of deeply-nested shapes.
 *
 *  When onPinPath is provided, each key shows a small "+col" affordance
 *  so the operator can promote that nested path to a top-level column
 *  in their table (B3 drill-in). The affordance flips to "✓" for paths
 *  the caller already considers pinned. */
function ObjectExpandPanel({
  value, maxExpandDepth, compact, pathPrefix, onPinPath, pinnedPaths,
}: {
  value: Record<string, unknown>;
  maxExpandDepth: number;
  compact?: boolean;
  pathPrefix?: string;
  onPinPath?: (path: string) => void;
  pinnedPaths?: Set<string>;
}): React.ReactElement {
  const entries = Object.entries(value);
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'auto auto 1fr', gap: '2px 8px',
      padding: '6px 10px', marginTop: 2,
      background: 'var(--bg-sunken, #f7f7f7)',
      border: '1px solid var(--line, #e0e0e0)',
      borderRadius: 4,
      fontSize: compact ? 11 : 11.5,
      maxWidth: 480, maxHeight: 360, overflowY: 'auto',
    }}>
      {entries.map(([k, v]) => {
        const fullPath = pathPrefix ? `${pathPrefix}.${k}` : k;
        const isPinned = pinnedPaths?.has(fullPath) ?? false;
        return (
          <React.Fragment key={k}>
            {onPinPath ? (
              <button
                type="button"
                onClick={(ev) => { ev.stopPropagation(); onPinPath(fullPath); }}
                title={isPinned ? `Unpin column ${fullPath}` : `Pin ${fullPath} as a column`}
                aria-label={isPinned ? 'Unpin column' : 'Pin as column'}
                style={{
                  background: 'transparent', border: 'none',
                  padding: '0 4px', cursor: 'pointer',
                  fontFamily: 'var(--mono, ui-monospace)',
                  fontSize: 11,
                  color: isPinned ? 'var(--accent, #0066cc)' : 'var(--ink-3, #888)',
                }}
              >{isPinned ? '✓' : '+'}</button>
            ) : <span />}
            <code style={{ fontFamily: 'var(--mono)', color: colorForTag(k, '').fg, fontWeight: 500 }}>{k}</code>
            <span style={{ minWidth: 0, overflow: 'hidden' }}>
              <NestedCell
                value={v}
                maxExpandDepth={maxExpandDepth - 1}
                compact={true}
                pathPrefix={fullPath}
                onPinPath={onPinPath}
                pinnedPaths={pinnedPaths}
              />
            </span>
          </React.Fragment>
        );
      })}
    </div>
  );
}
